import {Injectable} from '@angular/core';
import * as CmsPagesActions from './cmsPages.actions';
import {map, switchMap, take} from 'rxjs/operators';
import {CmsPagesPartialState} from './cmsPages.reducer';
import {Actions, createEffect} from '@ngrx/effects';
import {DataPersistence, withCorrelationIdOf} from '@kion/ngrx-helper';
import {CmsPage} from '@kion/data-models';
import {of} from 'rxjs';
import {CmsPagesQuery} from './cmsPages.selectors';
import {ActionCache} from '../../../../../helper/ngrx-helper/src/lib/action-cache';
import {CmsPageConnectors} from "./cmsPage.connectors";
import {NetworkPartnerConnectors} from "../../../../data-access-network-partners/src/lib/+state/networkPartner.connectors";

@Injectable()
export class CmsPagesEffects {
  loadCmsPage$ = createEffect(() =>
    this.dataPersistence.fetch(
      CmsPagesActions.loadCmsPage,
      {
        run: (
          action: ReturnType<typeof CmsPagesActions.loadCmsPage>
        ) => {
          return this.dataPersistence.store.select(CmsPagesQuery.getAllCmsPages).pipe(
            take(1),
            switchMap(
              pages => {
                const cmsPage = pages.find(page => page.label ? page.label === action.cmsPageId : page.uid === action.cmsPageId);
                const runAction = this.backend.loadPageByIdOrTicket(
                  action.cmsPageId,
                  action.isTicket,
                  action.cmsTicketId
                ).pipe(
                  map(
                    (cmsPage: CmsPage) => withCorrelationIdOf(
                      action,
                      CmsPagesActions.loadCmsPageSuccess({cmsPage: cmsPage})
                    )
                  )
                );
                if (!cmsPage) {
                  return runAction;
                }
                return this.actionCache.withCache(
                  action,
                  () => runAction,
                  () => of(
                    withCorrelationIdOf(
                      action,
                      CmsPagesActions.loadCmsPageSuccess({cmsPage: cmsPage})
                    )
                  )
                );
              }
            )
          );
        },
        onError: (
          action: ReturnType<typeof CmsPagesActions.loadCmsPage>,
          error: any
        ) => withCorrelationIdOf(
          action,
          CmsPagesActions.loadCmsPageError({error: error})
        )
      }
    )
  );
  loadProductCmsPage$ = createEffect(() =>
    this.dataPersistence.fetch(
      CmsPagesActions.loadProductCmsPage,
      {
        run: (
          action: ReturnType<typeof CmsPagesActions.loadProductCmsPage>
        ) => {
          return this.backend.loadPageByProductCode(action.code).pipe(
            map(
              (response: CmsPage) => withCorrelationIdOf(
                action,
                CmsPagesActions.loadProductCmsPageSuccess({cmsPage: response})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof CmsPagesActions.loadProductCmsPage>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            CmsPagesActions.loadProductCmsPageError({error: error})
          );
        }
      }
    )
  )
  loadNetworkPartnerTermsAndConditionsContent$ = createEffect(() =>
    this.dataPersistence.fetch(
      CmsPagesActions.loadNetworkPartnerTermsAndConditionsContent,
      {
        run: (
          action: ReturnType<typeof CmsPagesActions.loadNetworkPartnerTermsAndConditionsContent>
        ) => {
          return this.networkPartnerService.loadNetworkPartnerTermsAndConditionsContent(action.nwpId).pipe(
            map(
              (response: string) => withCorrelationIdOf(
                action,
                CmsPagesActions.loadNetworkPartnerTermsAndConditionsContentSuccess({
                  nwpId: action.nwpId,
                  termsAndConditions: response
                })
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof CmsPagesActions.loadNetworkPartnerTermsAndConditionsContent>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            CmsPagesActions.loadNetworkPartnerTermsAndConditionsContentError({error: error})
          );
        }
      }
    )
  )
  loadNetworkPartnerPrivacyPolicyContent$ = createEffect(() =>
    this.dataPersistence.fetch(
      CmsPagesActions.loadNetworkPartnerPrivacyPolicyContent,
      {
        run: (
          action: ReturnType<typeof CmsPagesActions.loadNetworkPartnerPrivacyPolicyContent>
        ) => {
          return this.networkPartnerService.loadNetworkPartnerPrivacyPolicyContent(action.nwpId).pipe(
            map(
              (response: string) => withCorrelationIdOf(
                action,
                CmsPagesActions.loadNetworkPartnerPrivacyPolicyContentSuccess({
                  nwpId: action.nwpId,
                  privacyPolicy: response
                })
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof CmsPagesActions.loadNetworkPartnerPrivacyPolicyContent>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            CmsPagesActions.loadNetworkPartnerPrivacyPolicyContentError({error: error})
          );
        }
      }
    )
  )

  constructor(
    private actions$: Actions,
    private dataPersistence: DataPersistence<CmsPagesPartialState>,
    private backend: CmsPageConnectors,
    private actionCache: ActionCache,
    private networkPartnerService: NetworkPartnerConnectors
  ) {
  }
}

